<script lang="ts">
	import { devices } from 'src/stores/lists/devices';
import { os } from 'src/stores/session/native-bridge';

	import { hapticFeedback } from '../../../services/native_container_requests_ios';

	import { get_gen_2_component_channel_structure, hasTilt } from '../../../helpers/interface_helpers';

	import { format } from 'svelte-i18n';

	import { SliderV2, Stack, toast } from '@Shelly/shelly-design-system';
	import { pin_unlocked_devices } from 'src/stores/device-runtime/pin-unlocked';
	import PinUnlock from '../../PinUnlock.svelte';
	import { unlockPin } from '../../../helpers/pin_lock_helpers';
	import { gen2Request } from '../../../services/gen_2_http_reqs';
	import { onSettingsResponse } from '../../../helpers/device_settings_helpers';
	import { capFirstChar } from 'src/helpers/utilities';

	export let device, status;
	export let settings = null;

	const haveMarks = true;
	const component = get_gen_2_component_channel_structure(device);

	let value = 0;
	let valueCache;
	let targetValue;

	let showModal = false;

	let callbackValue;

	let isLoading = false;

	let marks = [];

	// TODO For G1 device there are predefined 4 slots for favorite positions.
	// If they are not set, by default value will be 0.
	// Discuss what to do if user want to set 0 value as favorite.
	// For now filter marks with value different from 0.
	// For gen2 devices will leave 0 as valid favorite, because inside local web it is valid favorite

	let sliderFilteredMarks = [];
	let mappedMarks;
	let slatPosition = status?.[component]?.slat_pos || 50;
	let slatCache = slatPosition;

	$: uiData = {
		cover: settings?.sys?.ui_data?.cover || '',
		cover1: settings?.sys?.ui_data?.cover1 || '',
		slat_poses: settings?.sys?.ui_data?.slat_poses || '',
	};
	$: hasSlat = hasTilt(device) && settings?.[component]?.slat?.enable;

	$: pinLocked =
		typeof $pin_unlocked_devices[device.id] === 'undefined' && $devices[device.id]?.pin_lock;

	$: isRolling =
		(targetValue && status[component].current_pos !== targetValue) ||
		/^(opening|closing)$/.test(status[component].state);

	$: rollingDirection =
		status[component].state === 'stopped'
			? valueCache < value
				? 'opening'
				: 'closing'
			: status[component].state;

	$: if (!isRolling) {
		value = status[component].current_pos;
		slatPosition = status[component].slat_pos; //updating slat position
	}

	$: if (status[component].current_pos === targetValue) {
		targetValue = undefined;
		valueCache = undefined;
	}

	$: {
		const uiDataObject = Object.fromEntries(
			Object.entries(uiData)?.map((x) => [
				x?.[0],
				Array.isArray(x?.[1])
					? x?.[1]?.map((y) => +y)
					: x?.[1]
							?.split(',')
							?.map((y) => (y !== '' ? +y : undefined))
							.filter((y) => !Number.isNaN(y) && typeof y !== 'undefined'),
			]),
		);

		marks = uiDataObject;

		if (!arraysAreEqual(marks, sliderFilteredMarks)) {
			mappedMarks = uiDataObject
				? Object.fromEntries(
						Object.entries(uiDataObject)?.map((x) => [
							x[0],
							x?.[1]
								?.map((m, index): { value: number; dbIndex: number } => {
									return {
										...m,
										value: +m,
										dbIndex: index,
									};
								})
								.filter((m) => !Number.isNaN(m?.value)),
						]),
					)
				: undefined;
		}
	}

	const arraysAreEqual = (arr1: number[], arr2: number[]) => {
		//small refactor w/o loop
		if (!Array.isArray(arr1) || !Array.isArray(arr2)) {
			return false;
		}

		return JSON.stringify(arr1) === JSON.stringify(arr2);
	};

	const changePosition = async (selectedPosition: number, type: string = 'cover') => {
		if (!pinLocked) {
			const isCoverControl = type === 'cover';

			isLoading = true;
			const propToChange = isCoverControl ? 'pos' : 'slat_pos';

			if ($os === 'IOS') {
				hapticFeedback('buttonPressed');
			}

			const res = await gen2Request(device, 'Cover.GoToPosition', {
				id: device.channel,
				[propToChange]: selectedPosition,
			});

			if (res.isok) {
				if (isCoverControl) {
					valueCache = value;
				} else {
					slatCache = slatPosition;
				}
			} else {
				if (isCoverControl) {
					value = valueCache;
				} else {
					slatPosition = slatCache;
				}
				toast.open({
					type: 'danger',
					title: $format('device_control_failed_long'),
				});
			}
			isLoading = false;
		} else {
			callbackValue = { selectedPosition, type };
			showModal = true;
		}
	};

	const handleMarkChange = async (
		value: number,
		type: 'add' | 'delete' = 'add',
		functionality = 'cover',
	) => {
		let res;
		isLoading = true;

		const isTypeAdd = type === 'add';

		const _marks = marks;

		if (isTypeAdd) {
			_marks[functionality].push(value);
		} else {
			_marks[functionality].splice(value, 1);
		}
		_marks[functionality]?.map((x) => (x = x.toString())).join(',');
		marks = Object.fromEntries(
			Object.entries(_marks)?.map((x) => [
				x?.[0],
				Array.isArray(x?.[1]) ? x?.[1].join(',') : x?.[1],
			]),
		);
		// );

		let ui_data = {
			...uiData,
			...marks,
		};

		mappedMarks = marks;
		res = await gen2Request(device, 'Sys.SetConfig', {
			config: {
				ui_data,
			},
		});
		if (res.isok) {
			settings.sys.ui_data = ui_data;
			devices_settings.update_devices_settings(device.id, settings);
		}
		onSettingsResponse(res, device); //ToDo need keys for gen2
		isLoading = false;
	};

	const handlePin = () => {
		pinLocked = unlockPin(device, pinLocked);
		changePosition(callbackValue.selectedPosition, callbackValue.type);
		callbackValue = undefined;
	};

	const handleClose = () => {
		value = valueCache;
	};

	const handleDelete = async (e: CustomEvent, functionality: string) => {
		if (!pinLocked) {
			const deleteMarkData: { value?: number; dbIndex?: number } = Object.values(
				mappedMarks[functionality],
			).filter((m: { dbIndex: number; value: number }) => m.dbIndex === e.detail.index)[0];

			Object.values(marks).forEach((m, i) => {
				if (m.value === deleteMarkData.value) {
					deleteMarkData.dbIndex = i;
				}
			});
			await handleMarkChange(deleteMarkData.dbIndex, 'delete', functionality);
		} else {
			showModal = true;
		}
	};
	const a11yProps = {
		sliderAriaLabel: $format('label_set_position', { default: 'Set position' }),
		ariaLabel: $format('label_set_position', { default: 'Set position' }),
		ariaLabelValue: $format('label_set_position', { default: 'Set position' }),
	};
	const a11yPropsBlindsTilt = {
		sliderAriaLabel: $format('label_blinds_tilt', { default: 'Set position' }),
		ariaLabel: $format('label_blinds_tilt', { default: 'Set position' }),
		ariaLabelValue: $format('label_blinds_tilt', { default: 'Set position' }),
	};
</script>

{#if !hasSlat}
	<SliderV2
		id="slider-cjlj0e1"
		{a11yProps}
		class="flex-full-height mb-l justify-content-center"
		title={isRolling
			? capFirstChar($format(`state_${rollingDirection}`, { default: rollingDirection }))
			: capFirstChar($format('state_opened', { default: 'opened' }))}
		stopSaveDeleteMarks={device.shared}
		{value}
		marks={mappedMarks['cover']}
		{haveMarks}
		on:value_change={(e) => {
			changePosition(e.detail.value, 'cover');
		}}
		on:move={() => {
			if ($os === 'IOS') {
				hapticFeedback('selection');
			}
		}}
		{isLoading}
		on:new_mark={async (e) => {
			!device.shared && handleMarkChange(e.detail.value, undefined, 'cover');
		}}
		on:delete_mark={!device.shared && ((e) => handleDelete(e, 'cover'))}
	/>
{:else}
	<div class="flex-full-height flex-direction-row mb-m">
		<SliderV2
			id="slider-z6uk4fp"
			{a11yProps}
			title={isRolling
				? capFirstChar(
						$format(`state_${rollingDirection}`, {
							default: rollingDirection,
						}),
					)
				: capFirstChar($format('state_opened', { default: 'opened' }))}
			stopSaveDeleteMarks={device.shared}
			{value}
			marks={mappedMarks['cover']}
			{haveMarks}
			type="vertical"
			class="flex-full-height mb-l justify-content-center"
			on:value_change={(e) => {
				changePosition(e.detail.value, 'cover');
			}}
			on:move={() => {
				if ($os === 'IOS') {
					hapticFeedback('selection');
				}
			}}
			on:new_mark={async (e) => {
				!device.shared && handleMarkChange(e.detail.value, undefined, 'cover');
			}}
			on:delete_mark={!device.shared && ((e) => handleDelete(e, 'cover'))}
		/>
		<SliderV2
			id="slider-c723z5f"
			a11yProps={a11yPropsBlindsTilt}
			title={capFirstChar($format('label_blinds_tilt', { default: 'Blinds tilt' }))}
			stopSaveDeleteMarks={device.shared}
			value={slatPosition}
			marks={mappedMarks['slat_poses']}
			{haveMarks}
			type="thin-vertical"
			class="flex-full-height mb-l justify-content-center"
			on:value_change={(e) => {
				changePosition(e.detail.value, 'slat_poses');
			}}
			on:move={() => {
				if ($os === 'IOS') {
					hapticFeedback('selection');
				}
			}}
			on:new_mark={async (e) => {
				!device.shared && handleMarkChange(e.detail.value, undefined, 'slat_poses');
			}}
			on:delete_mark={!device.shared && ((e) => handleDelete(e, 'slat_poses'))}
		/>
	</div>
{/if}

{#if showModal}
	<PinUnlock
		bind:showModal
		on:unlock={handlePin}
		on:close={handleClose}
		on:cancel={() => (showModal = false)}
	/>
{/if}
